import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Box, Button, ButtonBase, Chip, Collapse, Typography } from '@mui/material'
import { restoreVersion } from './photoReviewApi'
import { downloadTarget } from './reviewModel'
import DownloadButton from './DownloadButton'
import { slotDisplayName } from '../photo-upload/PhotoUpload'

// The same checkerboard ShotCompare puts behind the edit: the thumbnails are
// transparent PNGs, and the cut-out is part of what a version is chosen for.
export const CHECKER = {
    backgroundColor: '#fff',
    backgroundImage: 'linear-gradient(45deg,#ddd 25%,transparent 25%),linear-gradient(-45deg,#ddd 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#ddd 75%),linear-gradient(-45deg,transparent 75%,#ddd 75%)',
    backgroundSize: '12px 12px',
    backgroundPosition: '0 0,0 6px,6px -6px,-6px 0',
}
const THUMB = 112

// Today's versions by time alone; older ones with their date.
function formatWhen(iso, language) {
    if (!iso) return ''
    const date = new Date(iso)
    const sameDay = date.toDateString() === new Date().toDateString()
    return sameDay
        ? date.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })
        : date.toLocaleString(language, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

// Every edit of a shot (the queue's `versions`, newest first), so the reviewer
// can go back to an earlier one. "Use this version" makes it the current
// version on the server; the normal Approve then signs it off. Each version
// can also be downloaded full size (Daniel's Photoshop round trip).
export default function VersionHistory({ sku, slot, fetchResource, disabled, onRestored }) {
    const { t, i18n } = useTranslation()
    const [open, setOpen] = useState(false)
    const [chosen, setChosen] = useState(null)
    const [restoring, setRestoring] = useState(false)
    const [error, setError] = useState(null)
    const versions = slot.versions || []
    if (versions.length < 2) return null

    const selected = versions.find(version => version.attemptNo === chosen)
    const usable = selected && selected.available && !selected.current

    async function handleRestore() {
        setRestoring(true)
        setError(null)
        // finally: a request that rejects outright must not leave the button
        // disabled for good.
        try {
            const response = await restoreVersion(fetchResource, slot.photoId, selected.attemptNo, slot.attempts)
            if (response?.error) setError(response.error)
            else setChosen(null)
        } catch {
            setError(t('photoReview.versions.failed'))
        } finally {
            setRestoring(false)
        }
        await onRestored()
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Button size='small' aria-expanded={open} onClick={() => setOpen(prev => !prev)}>
                {t('photoReview.versions.toggle', { count: versions.length })}
            </Button>
            <Collapse in={open} unmountOnExit>
                <Box component='ul' aria-label={t('photoReview.versions.listLabel')}
                    sx={{ listStyle: 'none', m: 0, mt: 1, p: 0, pb: 0.5, display: 'flex', gap: 1.5, overflowX: 'auto' }}>
                    {versions.map(version => {
                        const kind = t(`photoReview.versions.kinds.${version.kind}`)
                        const when = formatWhen(version.createdAt, i18n.language)
                        const isChosen = version.attemptNo === chosen
                        const name = [kind, when, version.current && t('photoReview.versions.current')].filter(Boolean).join(', ')
                        return (
                            <Box component='li' key={version.attemptNo} sx={{ flex: '0 0 auto', width: THUMB }}>
                                <ButtonBase
                                    aria-label={name}
                                    aria-pressed={isChosen}
                                    disabled={!version.available}
                                    onClick={() => setChosen(version.attemptNo)}
                                    sx={{
                                        display: 'block', width: THUMB, height: THUMB, borderRadius: 1, overflow: 'hidden',
                                        outline: theme => (version.current
                                            ? `2px solid ${theme.palette.primary.main}`
                                            : isChosen ? `2px dashed ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`),
                                        outlineOffset: version.current || isChosen ? 2 : 0,
                                        '&.Mui-focusVisible': { outline: theme => `3px solid ${theme.palette.primary.dark}` },
                                        ...(version.available && CHECKER),
                                    }}
                                >
                                    {version.available ? (
                                        <img
                                            src={`${process.env.REACT_APP_API_BASE_URL}${version.previewPath}`}
                                            alt={t('photoReview.versions.alt', { kind, when })}
                                            loading='lazy'
                                            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                                        />
                                    ) : (
                                        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, textAlign: 'center', color: 'text.secondary', border: 1, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                                            <Typography variant='caption'>{t('photoReview.versions.unavailable')}</Typography>
                                        </Box>
                                    )}
                                </ButtonBase>
                                <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 0.75 }}>
                                        <Typography variant='body2' data-testid='version-kind' sx={{ fontWeight: 600 }}>{kind}</Typography>
                                        <Typography variant='caption' data-testid='version-time' color='text.secondary'>{when}</Typography>
                                    </Box>
                                    {version.available && (
                                        <Box sx={{ mt: -0.5, mr: -0.5 }}>
                                            <DownloadButton
                                                iconOnly
                                                target={downloadTarget(sku, slot, version.attemptNo)}
                                                fetchResource={fetchResource}
                                                label={t('photoReview.versions.download', { kind, when })}
                                                onStart={() => setError(null)}
                                                onError={reason => setError(reason || t('photoReview.versions.downloadFailed'))}
                                            />
                                        </Box>
                                    )}
                                </Box>
                                {/* Gemini also saw this other shot of the piece for this version. */}
                                {version.detailReference && (
                                    <Typography variant='caption' component='div' color='text.secondary' sx={{ lineHeight: 1.3 }}>
                                        {t('photoReview.detailRef.chosen', { label: slotDisplayName(t, version.detailReference.slot) })}
                                    </Typography>
                                )}
                                {version.current && <Chip size='small' color='primary' label={t('photoReview.versions.current')} sx={{ mt: 0.5 }} />}
                            </Box>
                        )
                    })}
                </Box>
                {usable && (
                    <Button variant='outlined' size='small' sx={{ mt: 1 }} disabled={disabled || restoring} onClick={handleRestore}>
                        {t('photoReview.versions.use')}
                    </Button>
                )}
                {error && <Alert severity='error' sx={{ mt: 1 }} onClose={() => setError(null)}>{error}</Alert>}
            </Collapse>
        </Box>
    )
}
